import { Helmet } from "react-helmet-async";

interface BreadcrumbItem {
  name: string;
  url: string;
}

interface SEOProps {
  title: string;
  description: string;
  canonicalUrl?: string;
  ogImage?: string;
  ogType?: "website" | "article" | "video.other";
  keywords?: string;
  noindex?: boolean;
  breadcrumbs?: BreadcrumbItem[];
  structuredData?: object[];
}

// Site-wide constants
const SITE_NAME = "The Founders Lounge";
const SITE_URL = "https://www.thefoundersloungepodcast.com";
const DEFAULT_OG_IMAGE = `${SITE_URL}/og-image.jpg`;
const TWITTER_HANDLE = "@thefoundersloungepodcast";

// JSON-LD structured data for the podcast
const podcastSeriesSchema = {
  "@context": "https://schema.org",
  "@type": "PodcastSeries",
  name: "The Founders Lounge",
  description: "The Founders Lounge Podcast - Real conversations with successful entrepreneurs. Unfiltered insights, proven strategies, and the stories behind building thriving businesses.",
  url: SITE_URL,
  author: [
    {
      "@type": "Person",
      name: "Alex Palma",
      jobTitle: "Co-Host & Founder",
    },
    {
      "@type": "Person",
      name: "Brent Tinsley",
      jobTitle: "Co-Host & Founder",
    },
  ],
  publisher: {
    "@type": "Organization",
    name: "The Founders Lounge",
    logo: {
      "@type": "ImageObject",
      url: `${SITE_URL}/logo.png`,
    },
  },
  webFeed: "https://www.youtube.com/@theFoundersLoungePodcast",
  genre: ["Business", "Entrepreneurship", "Startups"],
  inLanguage: "en-US",
  image: DEFAULT_OG_IMAGE,
  sameAs: [
    "https://www.youtube.com/@theFoundersLoungePodcast",
    "https://www.instagram.com/thefoundersloungepodcast/",
    "https://www.facebook.com/thefoundersloungepodcast/",
    "https://www.linkedin.com/company/the-founders-lounge-podcast/",
    "https://www.tiktok.com/@thefoundersloungepodcast",
    "https://open.spotify.com/show/2h6PHEsBTYNyhy3Zcv2Oai",
  ],
};

// WebSite Schema with SearchAction for sitelinks search box
const websiteSchema = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  "@id": `${SITE_URL}/#website`,
  name: "The Founders Lounge",
  alternateName: "Founders Lounge Podcast",
  url: SITE_URL,
  description: "The Founders Lounge Podcast - Real conversations with successful entrepreneurs. Unfiltered insights, proven strategies, and the stories behind building thriving businesses.",
  publisher: {
    "@id": `${SITE_URL}/#organization`,
  },
  potentialAction: {
    "@type": "SearchAction",
    target: {
      "@type": "EntryPoint",
      urlTemplate: `${SITE_URL}/blog?q={search_term_string}`,
    },
    "query-input": "required name=search_term_string",
  },
  inLanguage: "en-US",
};

export const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [
    {
      "@type": "Question",
      name: "What is The Founders Lounge Podcast?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "The Founders Lounge is a podcast featuring real, unfiltered conversations with successful entrepreneurs. Hosted by Alex Palma and Brent Tinsley, we share proven strategies, insights, and the stories behind building thriving businesses.",
      },
    },
    {
      "@type": "Question",
      name: "How can I be a guest on The Founders Lounge Podcast?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "We're always looking for inspiring founders to share their stories. Visit our 'Be a Guest' page at foundersloungepodcast.com/apply to submit your application and tell us about your entrepreneurial journey.",
      },
    },
    {
      "@type": "Question",
      name: "Where can I listen to The Founders Lounge Podcast?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "You can watch The Founders Lounge on YouTube at youtube.com/@theFoundersLoungePodcast. We also share clips and content on Instagram, TikTok, Facebook, and LinkedIn.",
      },
    },
    {
      "@type": "Question",
      name: "Who hosts The Founders Lounge Podcast?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "The Founders Lounge is co-hosted by Alex Palma and Brent Tinsley, two entrepreneurs based in Texas who are passionate about sharing the real stories behind building successful businesses.",
      },
    },
    {
      "@type": "Question",
      name: "What topics does The Founders Lounge cover?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "We cover entrepreneurship, startup growth, business strategies, leadership, overcoming challenges, and the authentic stories of founders building companies. Our guests share real insights from their journeys.",
      },
    },
  ],
};

const organizationSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "@id": `${SITE_URL}/#organization`,
  name: "The Founders Lounge",
  alternateName: "Founders Lounge Podcast",
  url: SITE_URL,
  logo: {
    "@type": "ImageObject",
    "@id": `${SITE_URL}/#logo`,
    url: `${SITE_URL}/logo.png`,
    contentUrl: `${SITE_URL}/logo.png`,
    width: 512,
    height: 512,
    caption: "The Founders Lounge Podcast Logo",
  },
  image: {
    "@type": "ImageObject",
    url: DEFAULT_OG_IMAGE,
    width: 1200,
    height: 630,
  },
  description: "The Founders Lounge Podcast - A podcast featuring real conversations with successful entrepreneurs. For Founders, By Founders.",
  foundingDate: "2024",
  address: {
    "@type": "PostalAddress",
    addressLocality: "Houston",
    addressRegion: "Texas",
    addressCountry: "US",
  },
  contactPoint: {
    "@type": "ContactPoint",
    email: "foundersloungepodcast@gmail.com",
    contactType: "customer service",
    availableLanguage: "English",
  },
  founder: [
    {
      "@type": "Person",
      name: "Alex Palma",
    },
    {
      "@type": "Person",
      name: "Brent Tinsley",
    },
  ],
  sameAs: [
    "https://www.youtube.com/@theFoundersLoungePodcast",
    "https://www.instagram.com/thefoundersloungepodcast/",
    "https://www.facebook.com/thefoundersloungepodcast/",
    "https://www.linkedin.com/company/the-founders-lounge-podcast/",
    "https://www.tiktok.com/@thefoundersloungepodcast",
    "https://open.spotify.com/show/2h6PHEsBTYNyhy3Zcv2Oai",
  ],
};

// LocalBusiness Schema for local SEO
const localBusinessSchema = {
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  "@id": `${SITE_URL}/#localbusiness`,
  name: "The Founders Lounge Podcast",
  description: "Texas-based entrepreneur podcast featuring real conversations with successful founders, startup insights, and business strategies.",
  url: SITE_URL,
  logo: `${SITE_URL}/logo.png`,
  image: DEFAULT_OG_IMAGE,
  email: "foundersloungepodcast@gmail.com",
  address: {
    "@type": "PostalAddress",
    addressLocality: "Houston",
    addressRegion: "TX",
    addressCountry: "US",
  },
  geo: {
    "@type": "GeoCoordinates",
    latitude: 29.7604,
    longitude: -95.3698,
  },
  areaServed: {
    "@type": "State",
    name: "Texas",
  },
  priceRange: "Free",
  sameAs: [
    "https://www.youtube.com/@theFoundersLoungePodcast",
    "https://www.instagram.com/thefoundersloungepodcast/",
    "https://www.facebook.com/thefoundersloungepodcast/",
    "https://www.linkedin.com/company/the-founders-lounge-podcast/",
    "https://www.tiktok.com/@thefoundersloungepodcast",
    "https://open.spotify.com/show/2h6PHEsBTYNyhy3Zcv2Oai",
  ],
};

const SEO = ({
  title,
  description,
  canonicalUrl,
  ogImage = DEFAULT_OG_IMAGE,
  ogType = "website",
  keywords,
  noindex = false,
  breadcrumbs,
  structuredData,
}: SEOProps) => {
  const fullTitle = title.includes(SITE_NAME) ? title : `${title} | ${SITE_NAME}`;
  const fullCanonicalUrl = canonicalUrl ? `${SITE_URL}${canonicalUrl}` : SITE_URL;

  // Generate breadcrumb schema if breadcrumbs are provided
  const breadcrumbSchema = breadcrumbs
    ? {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: breadcrumbs.map((item, index) => ({
          "@type": "ListItem",
          position: index + 1,
          name: item.name,
          item: item.url.startsWith("http") ? item.url : `${SITE_URL}${item.url}`,
        })),
      }
    : null;

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={fullCanonicalUrl} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={fullCanonicalUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="en_US" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content={TWITTER_HANDLE} />
      <meta name="twitter:creator" content={TWITTER_HANDLE} />
      <meta name="twitter:url" content={fullCanonicalUrl} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      {/* JSON-LD Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(websiteSchema)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(podcastSeriesSchema)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(organizationSchema)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(localBusinessSchema)}
      </script>
      {breadcrumbSchema && (
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      )}
      {/* Additional structured data passed as props */}
      {structuredData?.map((schema, index) => (
        <script key={index} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
};

export default SEO;
